import type {
  EventDetail,
  EventListResponse,
  EventUpdateInput,
} from "@cdt/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { ApiError } from "@/lib/api";
import {
  approveEvent,
  clearManualReviewQueue,
  deleteEvent,
  rejectEvent,
  updateEvent,
} from "@/lib/eventsApi";
import { REVIEW_QUEUE_KEY } from "@/hooks/useReviewQueue";

interface ForbiddenBody {
  error: string;
  currentStatus: string;
  allowedStatuses: string[];
}

function isForbiddenBody(value: unknown): value is ForbiddenBody {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.currentStatus === "string" && Array.isArray(v.allowedStatuses)
  );
}

/**
 * Translate an ApiError into a user-facing toast. Each HTTP status carries a
 * different UX meaning; the 409 case invalidates the queue because the server
 * knows better than us what the current state is.
 */
function handleActionError(
  error: unknown,
  invalidate: () => void,
  fallback: string,
): void {
  if (error instanceof ApiError) {
    if (error.status === 409 && isForbiddenBody(error.body)) {
      toast.warning("Stato gia' cambiato da un altro utente, ricarico la coda");
      invalidate();
      return;
    }
    if (error.status === 422) {
      const body = error.body as { error?: string } | null;
      toast.error(body?.error ?? "Dati mancanti o non validi");
      return;
    }
    if (error.status === 404) {
      toast.warning("Evento non trovato, ricarico la coda");
      invalidate();
      return;
    }
    if (error.status === 400) {
      const body = error.body as { error?: string } | null;
      toast.error(body?.error ?? "Richiesta non valida");
      return;
    }
  }
  const msg = error instanceof Error ? error.message : String(error);
  toast.error(`${fallback}: ${msg}`);
}

function useInvalidateEvents() {
  const qc = useQueryClient();
  return () => {
    // Review queue (exact key) and any detail cached for the same event.
    qc.invalidateQueries({ queryKey: REVIEW_QUEUE_KEY });
    qc.invalidateQueries({ queryKey: ["events"] });
  };
}

/**
 * Bulk-clears the whole MANUAL_REVIEW queue for the current mailbox (soft:
 * status → IGNORED). Used by the "Svuota coda" action on the review page.
 */
export function useClearReviewQueue() {
  const qc = useQueryClient();
  const invalidate = useInvalidateEvents();

  return useMutation<{ updated: number }, unknown, void>({
    mutationFn: () => clearManualReviewQueue(),
    onSuccess: ({ updated }) => {
      toast.success(
        updated > 0
          ? `Coda svuotata: ${updated} ${updated === 1 ? "evento" : "eventi"} scartati`
          : "Coda già vuota",
      );
      invalidate();
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => handleActionError(err, invalidate, "Svuotamento coda fallito"),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  const invalidate = useInvalidateEvents();

  return useMutation<EventDetail, unknown, { id: string; patch: EventUpdateInput }>({
    mutationFn: ({ id, patch }) => updateEvent(id, patch),
    onSuccess: (data) => {
      // Populate the detail cache immediately so the open detail page shows
      // fresh data without waiting for the subsequent refetch.
      qc.setQueryData(["events", "detail", data.id], data);
      // Invalidate all event lists (review queue, events table with any filters)
      // and stats so badges/counters update without a manual page refresh.
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Modifiche salvate");
    },
    onError: (err) => handleActionError(err, invalidate, "Salvataggio fallito"),
  });
}

/**
 * Optimistically removes the event from the review queue, then reconciles
 * with the server response. On failure we restore the previous queue from
 * the snapshot and surface the error via toast.
 */
function useQueueRemovalMutation<TInput>(
  mutationFn: (input: TInput & { id: string }) => Promise<EventDetail>,
  successMessage: (data: EventDetail) => string,
  fallbackMessage: string,
) {
  const qc = useQueryClient();
  const invalidate = useInvalidateEvents();

  return useMutation<
    EventDetail,
    unknown,
    TInput & { id: string },
    { previous: EventListResponse | undefined }
  >({
    mutationFn,
    onMutate: async (variables) => {
      await qc.cancelQueries({ queryKey: REVIEW_QUEUE_KEY });
      const previous = qc.getQueryData<EventListResponse>(REVIEW_QUEUE_KEY);
      if (previous) {
        qc.setQueryData<EventListResponse>(REVIEW_QUEUE_KEY, {
          ...previous,
          items: previous.items.filter((it) => it.id !== variables.id),
          total: Math.max(0, previous.total - 1),
        });
      }
      return { previous };
    },
    onError: (err, _variables, context) => {
      if (context?.previous) {
        qc.setQueryData(REVIEW_QUEUE_KEY, context.previous);
      }
      handleActionError(err, invalidate, fallbackMessage);
    },
    onSuccess: (data) => {
      toast.success(successMessage(data));
      qc.setQueryData(["events", "detail", data.id], data);
    },
    onSettled: () => {
      invalidate();
    },
  });
}

export function useApproveEvent() {
  return useQueueRemovalMutation<{ id: string }>(
    ({ id }) => approveEvent(id),
    (data) =>
      data.status === "ERROR"
        ? "Approvazione registrata ma Outlook ha rifiutato: apri il dettaglio"
        : "Evento approvato e creato nel calendario",
    "Approvazione fallita",
  );
}

export function useRejectEvent() {
  return useQueueRemovalMutation<{ id: string; reason?: string }>(
    ({ id, reason }) => rejectEvent(id, reason),
    () => "Evento rifiutato",
    "Rifiuto fallito",
  );
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  const invalidate = useInvalidateEvents();

  return useMutation<EventDetail, unknown, { id: string }>({
    mutationFn: ({ id }) => deleteEvent(id),
    onSuccess: (data) => {
      qc.setQueryData(["events", "detail", data.id], data);
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Evento rimosso dal calendario e marcato come ignorato");
      invalidate();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 502) {
        const body = err.body as { error?: string } | null;
        toast.error(
          body?.error ?? "Impossibile rimuovere l'evento dal calendario Outlook",
        );
        return;
      }
      handleActionError(err, invalidate, "Eliminazione fallita");
    },
  });
}
